import { submitTicket } from '../api-client'
import type { EventCallback, SubmitTicketData } from '../types'

type T = (key: string) => string

interface PortalTicket {
  id: string
  ticketNumber: string
  title: string
  status: string
  priority: string
  createdAt: string
}

interface PortalTicketDetail extends PortalTicket {
  replies: Array<{
    id: string
    body: string
    authorName: string
    isStaff: boolean
    createdAt: string
  }>
}

export function createPortalView(
  t: T,
  fetchTickets: () => Promise<PortalTicket[]>,
  fetchTicketDetail: (id: string) => Promise<PortalTicketDetail>,
  submitReply: (ticketId: string, body: string) => Promise<void>,
  projectId: string,
  onTicketCreated: EventCallback,
  onError: EventCallback,
  pollingInterval?: number,
): HTMLElement {
  const container = document.createElement('div')
  container.className = 'rqd-portal'

  let currentView: 'list' | 'detail' | 'new' = 'list'
  let pollTimer: ReturnType<typeof setInterval> | undefined

  function renderList() {
    currentView = 'list'
    while (container.firstChild) container.removeChild(container.firstChild)

    const header = document.createElement('div')
    header.style.display = 'flex'
    header.style.justifyContent = 'space-between'
    header.style.alignItems = 'center'
    header.style.marginBottom = '12px'

    const title = document.createElement('span')
    title.style.fontWeight = '600'
    title.textContent = t('portal.myTickets')
    header.appendChild(title)

    const newBtn = document.createElement('button')
    newBtn.className = 'rqd-btn rqd-btn-primary'
    newBtn.style.width = 'auto'
    newBtn.style.padding = '6px 14px'
    newBtn.style.fontSize = '13px'
    newBtn.textContent = t('portal.newTicket')
    newBtn.addEventListener('click', renderNewTicketForm)
    header.appendChild(newBtn)

    container.appendChild(header)

    const loading = document.createElement('p')
    loading.textContent = '...'
    loading.style.textAlign = 'center'
    loading.style.color = 'var(--rqd-text-secondary)'
    container.appendChild(loading)

    fetchTickets()
      .then((tickets) => {
        loading.remove()
        if (tickets.length === 0) {
          const empty = document.createElement('p')
          empty.textContent = t('portal.noTickets')
          empty.style.textAlign = 'center'
          empty.style.color = 'var(--rqd-text-secondary)'
          empty.style.padding = '24px 0'
          container.appendChild(empty)
          return
        }

        for (const ticket of tickets) {
          const row = document.createElement('div')
          row.className = 'rqd-ticket-info'
          row.style.cursor = 'pointer'
          row.style.marginBottom = '8px'

          const top = document.createElement('div')
          top.style.display = 'flex'
          top.style.justifyContent = 'space-between'
          top.style.marginBottom = '4px'

          const num = document.createElement('span')
          num.style.fontWeight = '600'
          num.style.fontSize = '13px'
          num.textContent = ticket.ticketNumber
          top.appendChild(num)

          const badge = document.createElement('span')
          badge.className = 'rqd-badge'
          badge.textContent = ticket.status
          top.appendChild(badge)

          row.appendChild(top)

          const titleEl = document.createElement('div')
          titleEl.style.fontSize = '14px'
          titleEl.textContent = ticket.title
          row.appendChild(titleEl)

          const date = document.createElement('div')
          date.style.fontSize = '12px'
          date.style.color = 'var(--rqd-text-secondary)'
          date.style.marginTop = '4px'
          date.textContent = new Date(ticket.createdAt).toLocaleDateString()
          row.appendChild(date)

          row.addEventListener('click', () => renderDetail(ticket.id))
          container.appendChild(row)
        }
      })
      .catch((err) => {
        loading.remove()
        onError(err)
      })

    // Start polling
    if (pollingInterval && pollingInterval > 0) {
      stopPolling()
      pollTimer = setInterval(() => {
        if (currentView === 'list') renderList()
      }, pollingInterval)
    }
  }

  function renderDetail(ticketId: string) {
    currentView = 'detail'
    stopPolling()
    while (container.firstChild) container.removeChild(container.firstChild)

    const backBtn = document.createElement('button')
    backBtn.className = 'rqd-btn rqd-btn-secondary'
    backBtn.textContent = t('tracker.back')
    backBtn.style.marginBottom = '12px'
    backBtn.addEventListener('click', renderList)
    container.appendChild(backBtn)

    const loading = document.createElement('p')
    loading.textContent = '...'
    loading.style.textAlign = 'center'
    container.appendChild(loading)

    fetchTicketDetail(ticketId)
      .then((ticket) => {
        loading.remove()

        const info = document.createElement('div')
        info.className = 'rqd-ticket-info'

        const titleRow = document.createElement('div')
        titleRow.style.fontWeight = '600'
        titleRow.style.marginBottom = '8px'
        titleRow.textContent = `${ticket.ticketNumber} — ${ticket.title}`
        info.appendChild(titleRow)

        for (const [label, value] of [
          [t('tracker.status'), ticket.status],
          [t('tracker.priority'), ticket.priority],
        ]) {
          const row = document.createElement('div')
          row.className = 'rqd-ticket-row'
          const lbl = document.createElement('span')
          lbl.className = 'rqd-ticket-label'
          lbl.textContent = label
          row.appendChild(lbl)
          const badge = document.createElement('span')
          badge.className = 'rqd-badge'
          badge.textContent = value
          row.appendChild(badge)
          info.appendChild(row)
        }
        container.appendChild(info)

        // Replies
        const repliesLabel = document.createElement('div')
        repliesLabel.className = 'rqd-label'
        repliesLabel.textContent = t('tracker.replies')
        repliesLabel.style.marginBottom = '8px'
        container.appendChild(repliesLabel)

        if (ticket.replies.length === 0) {
          const noReplies = document.createElement('p')
          noReplies.textContent = t('tracker.noReplies')
          noReplies.style.color = 'var(--rqd-text-secondary)'
          noReplies.style.fontSize = '13px'
          container.appendChild(noReplies)
        } else {
          for (const reply of ticket.replies) {
            const replyEl = document.createElement('div')
            replyEl.className = 'rqd-reply'

            const header = document.createElement('div')
            header.className = 'rqd-reply-header'
            const authorSpan = document.createElement('span')
            authorSpan.textContent = reply.authorName
            if (reply.isStaff) authorSpan.className = 'rqd-reply-staff'
            header.appendChild(authorSpan)
            const dateSpan = document.createElement('span')
            dateSpan.textContent = new Date(reply.createdAt).toLocaleString()
            header.appendChild(dateSpan)
            replyEl.appendChild(header)

            const body = document.createElement('div')
            body.className = 'rqd-reply-body'
            body.textContent = reply.body
            replyEl.appendChild(body)

            container.appendChild(replyEl)
          }
        }

        // Reply form
        const replyGroup = document.createElement('div')
        replyGroup.className = 'rqd-form-group'
        replyGroup.style.marginTop = '12px'

        const textarea = document.createElement('textarea')
        textarea.className = 'rqd-textarea'
        textarea.placeholder = t('tracker.replyPlaceholder')
        textarea.rows = 3
        replyGroup.appendChild(textarea)
        container.appendChild(replyGroup)

        const sendBtn = document.createElement('button')
        sendBtn.className = 'rqd-btn rqd-btn-primary'
        sendBtn.textContent = t('tracker.sendReply')
        sendBtn.addEventListener('click', async () => {
          const body = textarea.value.trim()
          if (!body) return
          sendBtn.disabled = true
          sendBtn.textContent = t('tracker.sending')
          try {
            await submitReply(ticketId, body)
            renderDetail(ticketId)
          } catch (err) {
            onError(err)
            sendBtn.disabled = false
            sendBtn.textContent = t('tracker.sendReply')
          }
        })
        container.appendChild(sendBtn)
      })
      .catch((err) => {
        loading.remove()
        onError(err)
      })
  }

  function renderNewTicketForm() {
    currentView = 'new'
    stopPolling()
    while (container.firstChild) container.removeChild(container.firstChild)

    const backBtn = document.createElement('button')
    backBtn.className = 'rqd-btn rqd-btn-secondary'
    backBtn.textContent = t('tracker.back')
    backBtn.style.marginBottom = '12px'
    backBtn.addEventListener('click', renderList)
    container.appendChild(backBtn)

    // Minimal inline form
    const fields: Record<string, HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = {}

    function addField(name: string, type: 'input' | 'textarea', label: string, placeholder: string) {
      const group = document.createElement('div')
      group.className = 'rqd-form-group'
      const lbl = document.createElement('label')
      lbl.className = 'rqd-label'
      lbl.textContent = label
      group.appendChild(lbl)
      const el = type === 'textarea' ? document.createElement('textarea') : document.createElement('input')
      el.className = type === 'textarea' ? 'rqd-textarea' : 'rqd-input'
      el.placeholder = placeholder
      el.name = name
      group.appendChild(el)
      fields[name] = el
      container.appendChild(group)
    }

    addField('title', 'input', t('form.title'), t('form.titlePlaceholder'))
    addField('description', 'textarea', t('form.description'), t('form.descriptionPlaceholder'))

    const submitBtn = document.createElement('button')
    submitBtn.className = 'rqd-btn rqd-btn-primary'
    submitBtn.textContent = t('form.submit')
    submitBtn.addEventListener('click', async () => {
      const title = (fields.title as HTMLInputElement).value.trim()
      if (!title || title.length < 5) return

      submitBtn.disabled = true
      submitBtn.textContent = t('form.submitting')

      const data: SubmitTicketData = {
        title,
        description: (fields.description as HTMLTextAreaElement).value.trim() || undefined,
        email: '', // Portal users are identified, email comes from customer config
        priority: 'medium',
      }

      try {
        const result = await submitTicket(projectId, data)
        onTicketCreated(result)
        renderList()
      } catch (err) {
        onError(err)
        submitBtn.disabled = false
        submitBtn.textContent = t('form.submit')
      }
    })
    container.appendChild(submitBtn)
  }

  function stopPolling() {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = undefined
    }
  }

  // Initial render
  renderList()

  return container
}
